import { useState, useRef, useCallback, useEffect } from 'react';
import { createSSEConnection, type SSEMessageEvent } from '@/api/chat';
import { useChatStore } from '@/stores/useChatStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { SSE_EVENT_TYPES } from '@/utils/constants';
import type { ModelLabel } from '@/types/session';
import { useAuthStore } from '@/stores/useAuthStore';

export interface SafetyAlert {
  open: boolean;
  message: string;
}

/**
 * 核心 Hook：管理 SSE 流式聊天连接。
 *
 * @param sessionId 当前会话 ID
 * @returns 控制句柄与状态
 */
export function useStreamingChat(sessionId: string | null) {
  const [safetyAlert, setSafetyAlert] = useState<SafetyAlert>({ open: false, message: '' });
  const permissionVersion = useAuthStore((s) => s.permissionVersion);
  const disposed = useRef(false);
  const retryTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const connectionRef = useRef<Partial<Record<ModelLabel | 'both', { close: () => void }>>>({});
  const reconnectCountRef = useRef(0);
  const maxReconnect = 3;
  const chunkBuffer = useRef<Record<string, string>>({ left: '', right: '' });
  const runIds = useRef<Record<ModelLabel | 'both', number>>({ left: 0, right: 0, both: 0 });

  const appendChunk = useChatStore((s) => s.appendChunk);
  const finalizeStream = useChatStore((s) => s.finalizeStream);
  const failStream = useChatStore((s) => s.failStream);
  const applySavedMessageId = useChatStore((s) => s.applySavedMessageId);
  const setStreaming = useChatStore((s) => s.setStreaming);
  const setError = useChatStore((s) => s.setError);
  const updateSessionTitle = useSessionStore((s) => s.updateSessionTitle);

  const modelsForTarget = useCallback((target: ModelLabel | 'both'): ModelLabel[] => {
    return target === 'both' ? ['left', 'right'] : [target];
  }, []);

  const closeConnection = useCallback((target?: ModelLabel | 'both') => {
    const keys: Array<ModelLabel | 'both'> = target ? [target] : ['left', 'right', 'both'];
    keys.forEach((key) => {
      runIds.current[key]++;
      connectionRef.current[key]?.close();
      delete connectionRef.current[key];
    });
    if (target === 'both' || !target) {
      runIds.current.left++;
      runIds.current.right++;
      connectionRef.current.left?.close();
      connectionRef.current.right?.close();
      delete connectionRef.current.left;
      delete connectionRef.current.right;
    }
  }, []);

  const startStream = useCallback(
    (userInput: string, target: ModelLabel | 'both' = 'both') => {
      if (!sessionId) {
        setError('未选择会话');
        return;
      }

      closeConnection(target);
      const runId = runIds.current[target];
      const isCurrent = () => !disposed.current && useSessionStore.getState().currentSession?.session_id === sessionId && runIds.current[target] === runId;
      reconnectCountRef.current = 0;
      modelsForTarget(target).forEach((model) => {
        chunkBuffer.current[model] = '';
      });

      const params = new URLSearchParams({
        session_id: sessionId,
        content: userInput,
        target,
      });
      const token = localStorage.getItem('deepflow_token');
      if (token) params.set('access_token', token);

      const url = `${import.meta.env.VITE_API_BASE_URL || '/api'}/chat/stream?${params.toString()}`;

      const connect = () => {
        if (!isCurrent()) return;
        const handle = createSSEConnection(
          url,
          (event: SSEMessageEvent) => {
            if (!isCurrent()) return;
            if (event.type === SSE_EVENT_TYPES.CHUNK && (event.model === 'left' || event.model === 'right')) {
              appendChunk(event.model, event.content || '', {
                brainRegion: event.brainRegion,
                stage: event.stage,
              });
              chunkBuffer.current[event.model] += event.content || '';
            } else if (event.type === SSE_EVENT_TYPES.DONE) {
              const models: ModelLabel[] =
                event.model === 'both'
                  ? modelsForTarget(target)
                  : event.model === 'left' || event.model === 'right'
                  ? [event.model]
                  : [];
              models.forEach((model) => {
                finalizeStream(model, chunkBuffer.current[model]);
                setStreaming(model, false);
              });
            } else if (event.type === SSE_EVENT_TYPES.ERROR) {
              if (event.model === 'left' || event.model === 'right') {
                failStream(event.model);
              } else {
                modelsForTarget(target).forEach(failStream);
              }
              setError(event.content || '流式响应出错');
            } else if (event.type === 'message_saved' && event.message_id && (event.model === 'left' || event.model === 'right')) {
              applySavedMessageId(event.model, event.message_id);
            } else if (event.type === 'session_title' && event.title && sessionId) {
              updateSessionTitle(sessionId, event.title);
            } else if (event.type === SSE_EVENT_TYPES.SAFETY_ALERT) {
              setSafetyAlert({ open: true, message: event.content || '检测到安全风险' });
              modelsForTarget(target).forEach((model) => setStreaming(model, false));
            }
          },
          (error: Error) => {
            if (!isCurrent()) return;
            if (reconnectCountRef.current < maxReconnect) {
              reconnectCountRef.current += 1;
              retryTimers.current.push(setTimeout(connect, 1000 * reconnectCountRef.current));
            } else {
              setError(error.message);
              modelsForTarget(target).forEach(failStream);
            }
          },
          () => {
            if (!isCurrent()) return;
            // 连接关闭
            modelsForTarget(target).forEach((model) => setStreaming(model, false));
            delete connectionRef.current[target];
          }
        );

        connectionRef.current[target] = handle;
      };

      connect();
    },
    [sessionId, appendChunk, finalizeStream, failStream, applySavedMessageId, setStreaming, setError, updateSessionTitle, closeConnection, modelsForTarget]
  );

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      retryTimers.current.forEach(clearTimeout);
      retryTimers.current = [];
      closeConnection();
    };
  }, [sessionId, permissionVersion, closeConnection]);

  const closeSafetyAlert = useCallback(() => {
    setSafetyAlert({ open: false, message: '' });
  }, []);

  return {
    startStream,
    closeConnection,
    safetyAlert,
    closeSafetyAlert,
  };
}
